<template>
  <div class="merchandise-landing-container">
    <template v-if="loaded">
      <div class="merchandise-registration-container">
        <span class="has-text-grey-icon">{{
          $t("af:merchandise.landing.welcome.title")
        }}</span>

        <transition
          name="fade"
          mode="in-out"
          :duration="{ enter: 2000, leave: 0 }"
        >
          <template v-if="currentStep === STEP.LANDING">
            <form @submit.prevent="submitLanding">
              <p class="is-size-3 has-text-weight-bold">
                <span v-if="landingMode === LANDING_MODE.REGISTRATION">
                  {{ $t("af:merchandise.landing.registration.title") }}</span
                >
                <span v-if="landingMode === LANDING_MODE.LOGIN">
                  {{ $t("af:merchandise.landing.login.title") }}</span
                >
                <span v-if="landingMode === LANDING_MODE.FORGOT_PASSWORD">
                  {{
                    $t("af:merchandise.landing.forgotten_password.title")
                  }}</span
                >
              </p>

              <validation-observer ref="regObserver">
                <BInputWithValidation
                  v-if="nameOnReg && landingMode === LANDING_MODE.REGISTRATION"
                  v-model="lastName"
                  rules="required"
                  :label="
                    $t(
                      'af:merchandise.landing.registration.input.last_name.label'
                    )
                  "
                  label-position="on-border"
                />

                <BInputWithValidation
                  v-if="nameOnReg && landingMode === LANDING_MODE.REGISTRATION"
                  v-model="firstName"
                  rules="required"
                  :label="
                    $t(
                      'af:merchandise.landing.registration.input.first_name.label'
                    )
                  "
                  label-position="on-border"
                />

                <p class="nonce-info has-text-grey-icon">
                  <span v-if="landingMode !== LANDING_MODE.FORGOT_PASSWORD">
                    {{ $t("af:merchandise.landing.registration.subtitle") }}
                  </span>
                  <span v-else>
                    {{
                      $t("af:merchandise.landing.forgotten_password.subtitle")
                    }}
                  </span>
                </p>

                <phone-input
                  :label="
                    $t(
                      'af:merchandise.landing.registration.input.phone_number.label'
                    )
                  "
                  v-model="phone"
                  v-if="landingMode !== LANDING_MODE.FORGOT_PASSWORD"
                />

                <b-field
                  v-if="landingMode === LANDING_MODE.REGISTRATION"
                  :label="
                    $t(
                      'af:merchandise.landing.registration.input.password.label'
                    )
                  "
                  label-position="on-border"
                >
                  <PasswordCheckerInput
                    v-model="password"
                    @validated="passwordValid = $event"
                  />
                </b-field>

                <template v-if="landingMode === LANDING_MODE.LOGIN">
                  <ValidationProvider
                    rules="required"
                    v-slot="{ errors, validated, valid }"
                  >
                    <b-field
                      :label="
                        $t(
                          'af:merchandise.landing.registration.input.password.label'
                        )
                      "
                      label-position="on-border"
                      class="mb-5"
                      :type="{
                        'is-danger': errors[0],
                        'is-success': validated && valid,
                      }"
                      :message="errors"
                    >
                      <b-input
                        type="password"
                        autocomplete="new-password"
                        password-reveal
                        v-model="password"
                      ></b-input>
                    </b-field>
                  </ValidationProvider>

                  <div class="has-text-right forgot-password">
                    <span
                      class="has-text-grey-icon is-clickable"
                      @click="landingMode = LANDING_MODE.FORGOT_PASSWORD"
                      >{{
                        $t(
                          "af:merchandise.landing.login.footer.button.forgotten_password"
                        )
                      }}</span
                    >
                  </div>
                </template>

                <template v-if="landingMode === LANDING_MODE.FORGOT_PASSWORD">
                  <ValidationProvider
                    rules="required"
                    v-slot="{ errors, valid }"
                  >
                    <b-field
                      :label="
                        $t(
                          'af:merchandise.landing.forgotten_password.input.phone_or_email.label'
                        )
                      "
                      label-position="on-border"
                      class="mb-5"
                      :type="{ 'is-danger': errors[0], 'is-success': valid }"
                      :message="errors"
                    >
                      <b-input
                        key="forgottenPasswordUser"
                        v-model="forgottenPasswordUser"
                      ></b-input>
                    </b-field>
                  </ValidationProvider>
                </template>

                <template
                  v-if="
                    regConfigsLoaded &&
                    landingMode === LANDING_MODE.REGISTRATION
                  "
                >
                  <statements-acceptance
                    :statements="regConfigs"
                    layout-type="REG"
                    read-required-validation-message="af:merchandise.landing.message.validation.statement_read_required"
                    ref="statementAcceptances"
                    :is-public="true"
                  />
                </template>
              </validation-observer>

              <b-field>
                <b-button
                  type="is-blue"
                  native-type="submit"
                  :loading="loading"
                  expanded
                >
                  <span v-if="landingMode === LANDING_MODE.REGISTRATION"
                    >{{
                      $t(
                        "af:merchandise.landing.registration.button.register.label"
                      )
                    }}
                  </span>
                  <span v-if="landingMode === LANDING_MODE.LOGIN"
                    >{{ $t("af:merchandise.landing.login.button.login.label") }}
                  </span>
                  <span v-if="landingMode === LANDING_MODE.FORGOT_PASSWORD"
                    >{{
                      $t(
                        "af:merchandise.landing.forgotten_password.button.next"
                      )
                    }}
                  </span>
                </b-button>
              </b-field>

              <div>
                <span v-if="landingMode === LANDING_MODE.REGISTRATION">{{
                  $t("af:merchandise.landing.registration.footer.title")
                }}</span>
                <span v-else>{{
                  $t("af:merchandise.landing.login.footer.title")
                }}</span>

                <span
                  class="af-text-button has-text-blue"
                  @click="
                    landingMode =
                      landingMode === LANDING_MODE.REGISTRATION
                        ? LANDING_MODE.LOGIN
                        : LANDING_MODE.REGISTRATION
                  "
                >
                  <span v-if="landingMode === LANDING_MODE.REGISTRATION"
                    >{{
                      $t(
                        "af:merchandise.landing.registration.footer.button.to_login"
                      )
                    }}
                  </span>
                  <span v-else
                    >{{
                      $t(
                        "af:merchandise.landing.login.footer.button.to_registration"
                      )
                    }}
                  </span>
                </span>
              </div>
            </form>
          </template>
        </transition>

        <transition
          name="fade"
          mode="in-out"
          :duration="{ enter: 2000, leave: 0 }"
        >
          <template v-if="currentStep === STEP.SECOND_FACTOR">
            <form
              @submit.prevent="submitSecondFactor"
              v-if="twoFactorMode !== TWO_FACTOR_MODE.FORGOT_PASSWORD"
            >
              <p class="is-size-3 has-text-weight-bold">
                {{ $t("af:merchandise.landing.confirmation.title") }}
              </p>

              <p class="nonce-info has-text-grey-icon">
                {{ $t("af:merchandise.landing.confirmation.subtitle") }}
              </p>

              <p
                class="nonce-info has-text-grey-icon"
                v-if="
                  twoFactorMode === TWO_FACTOR_MODE.PASSWORD_CHANGE_REQUIRED
                "
              >
                {{
                  $t("af:merchandise.landing.password_change_required.title")
                }}
              </p>

              <validation-observer ref="codeObserver">
                <BInputWithValidation
                  :label="
                    $t(
                      'af:merchandise.landing.confirmation.input.twofactor.label'
                    )
                  "
                  rules="required"
                  label-position="on-border"
                  v-model="secondFactorAnswer.code"
                />

                <b-field
                  :label="
                    $t(
                      'af:merchandise.landing.confirmation.input.new_password.label'
                    )
                  "
                  rules="required"
                  label-position="on-border"
                  class="mb-5"
                  v-if="
                    twoFactorMode === TWO_FACTOR_MODE.PASSWORD_CHANGE_REQUIRED
                  "
                >
                  <PasswordCheckerInput
                    v-model="newPassword"
                    @validated="newPasswordValid = $event"
                  />
                </b-field>
              </validation-observer>

              <b-field>
                <b-button
                  type="is-blue"
                  native-type="submit"
                  :loading="loading"
                  expanded
                >
                  {{
                    $t("af:merchandise.landing.confirmation.button.login.label")
                  }}
                </b-button>
              </b-field>
            </form>

            <form
              @submit.prevent="submitPasswordForgottenFinish"
              v-if="twoFactorMode === TWO_FACTOR_MODE.FORGOT_PASSWORD"
            >
              <validation-observer ref="forgottenPasswordFinishObserver">
                <template v-if="looksLikeAnEmail">
                  <img
                    class="mx-auto is-block"
                    :src="require('@/assets/images/mail-sent.svg')"
                    alt=""
                  />

                  <span class="is-size-3 has-text-weight-bold">
                    {{
                      $t(
                        "af:merchandise.landing.forgotten_password.email.title"
                      )
                    }}
                  </span>

                  <p class="info has-text-grey-icon mb-6">
                    {{
                      $t(
                        "af:merchandise.landing.forgotten_password.email.subtitle"
                      )
                    }}
                  </p>
                </template>

                <template v-else>
                  <img
                    class="mx-auto is-block mb-4"
                    :src="require('@/assets/images/two-factor-phone.svg')"
                    alt=""
                  />

                  <span class="is-size-3 has-text-weight-bold">
                    {{
                      $t(
                        "af:merchandise.landing.forgotten_password.phone.title"
                      )
                    }}
                  </span>

                  <p class="info has-text-grey-icon mb-6">
                    {{
                      $t(
                        "af:merchandise.landing.forgotten_password.phone.subtitle"
                      )
                    }}
                  </p>
                </template>

                <BInputWithValidation
                  :label="
                    $t(
                      'af:merchandise.landing.forgotten_password.input.security_code.label'
                    )
                  "
                  rules="required"
                  label-position="on-border"
                  autocomplete="new-password"
                  v-model="secondFactorAnswer.code"
                />

                <b-field
                  :label="
                    $t(
                      'af:merchandise.landing.forgotten_password.input.new_password.label'
                    )
                  "
                  rules="required"
                  label-position="on-border"
                >
                  <PasswordCheckerInput
                    v-model="newPassword"
                    @validated="passwordValid = $event"
                  />
                </b-field>

                <b-field>
                  <b-button
                    type="is-blue"
                    native-type="submit"
                    expanded
                    :loading="finishLoading"
                    >{{
                      $t(
                        "af:merchandise.landing.forgotten_password.button.submit"
                      )
                    }}
                  </b-button>
                </b-field>
              </validation-observer>
            </form>
          </template>
        </transition>

        <transition
          name="fade"
          mode="in-out"
          :duration="{ enter: 2000, leave: 0 }"
        >
          <template v-if="currentStep === STEP.SUMMARY">
            <form @submit.prevent="submitNext()">
              <p class="is-size-3 has-text-weight-bold">
                <span v-if="landingMode === LANDING_MODE.REGISTRATION">{{
                  $t(
                    "af:merchandise.landing.summary.successful_registration.title"
                  )
                }}</span>
                <span v-else>{{
                  $t("af:merchandise.landing.summary.successful_login.title")
                }}</span>
              </p>

              <div class="nonce-info has-text-grey-icon is-flex">
                <img
                  class="is-block"
                  :src="require('@/assets/images/checkout-success.svg')"
                  alt="registration-success"
                />
                <div class="ml-5 mt-3 is-flex is-flex-direction-column">
                  <label v-if="landingMode === LANDING_MODE.REGISTRATION">
                    {{
                      $t(
                        "af:merchandise.landing.summary.successful_registration.subtitle.line_one"
                      )
                    }}
                  </label>
                  <label v-else>
                    {{
                      $t(
                        "af:merchandise.landing.summary.successful_login.subtitle.line_one"
                      )
                    }}
                  </label>

                  <span class="mt-4">
                    <label v-if="landingMode === LANDING_MODE.REGISTRATION">
                      {{
                        $t(
                          "af:merchandise.landing.summary.successful_registration.subtitle.line_two"
                        )
                      }}
                    </label>
                    <label v-else>
                      {{
                        $t(
                          "af:merchandise.landing.summary.successful_login.subtitle.line_two"
                        )
                      }}
                    </label>
                  </span>
                </div>
              </div>

              <b-field>
                <b-button
                  type="is-blue"
                  native-type="submit"
                  :loading="loading"
                  expanded
                >
                  {{ $t("af:merchandise.landing.summary.button.next.label") }}
                </b-button>
              </b-field>
            </form>
          </template>
        </transition>
      </div>

      <div
        class="merchandise-basket-container"
        :class="{
          'merchandise-basket-container-shrink': currentStep === STEP.SUMMARY,
          'merchandise-basket-container-pushed-right':
            shrinkTransitionCompleted,
        }"
      >
        <!-- Basket content -->
        <BasketContent v-if="!linkingError" :purchase="purchase">
          <p
            class="has-text-grey-icon mt-5"
            v-if="landingMode === LANDING_MODE.REGISTRATION"
          >
            {{ $t("af:merchandise.basket.registration.label") }}
          </p>
        </BasketContent>

        <!-- Linking error -->
        <transition name="fade" mode="in-out">
          <div class="info-box has-error is-flex" v-if="linkingError">
            <b-icon
              icon-pack="fa"
              icon="exclamation-circle"
              size="is-large"
              class="mr-3"
            ></b-icon>
            <div>
              <p class="has-text-weight-bold mb-0 mt-2">
                {{ $t("af:merchandise.landing.summary.error_box.title") }}
              </p>
              <p>
                {{ $t("af:merchandise.landing.summary.error_box.subtitle") }}
              </p>
            </div>
          </div>
        </transition>

        <!-- Linking success -->
        <transition name="fade" mode="in-out">
          <div class="info-box is-flex" v-if="moveTransitionCompleted">
            <b-icon
              icon-pack="fa"
              icon="exclamation-circle"
              size="is-large"
              class="mr-3"
            ></b-icon>
            <div>
              <p class="has-text-weight-bold mb-0 mt-2">
                {{
                  $t(
                    "af:merchandise.landing.successful_registration.info_box.title"
                  )
                }}
              </p>
              <p>
                {{
                  $t(
                    "af:merchandise.landing.successful_registration.info_box.subtitle"
                  )
                }}
              </p>
            </div>
          </div>
        </transition>
      </div>

      <BasketButton
        :class="{
          'basket-opened': moveTransitionCompleted,
        }"
      ></BasketButton>
    </template>
  </div>
</template>

<script>
import BInputWithValidation from "../components/BInputWithValidation";
import registrationMixin from "../mixins/registrationMixin";
import { resetValidator } from "../utils/util";
import BasketContent from "../components/basket/BasketContent";
import i18n from "../plugins/i18n";
import twoFactorMixin from "../mixins/twoFactorMixin";
import { LOGIN_STATUS } from "../utils/const";
import forgotPasswordMixin from "../mixins/forgotPasswordMixin";
import Vue from "vue";
import { getActiveLandingParams } from "../utils/landingParamUtil";

export const STEP = {
  LANDING: 0,
  SECOND_FACTOR: 1,
  SUMMARY: 2,
};

export const LANDING_MODE = {
  REGISTRATION: 0,
  LOGIN: 1,
  FORGOT_PASSWORD: 2,
};

export const TWO_FACTOR_MODE = {
  REGISTRATION: 0,
  LOGIN: 1,
  PASSWORD_CHANGE_REQUIRED: 2,
  FORGOT_PASSWORD: 3,
};

export default {
  name: "Checkout",
  props: {
    purchaseId: {
      type: String,
      required: true,
    },
  },
  title: "af:page.merchandise.registration.title",
  mixins: [registrationMixin, twoFactorMixin, forgotPasswordMixin],
  components: {
    BasketContent,
    BInputWithValidation,
    PasswordCheckerInput: () => import("@/components/PasswordCheckerInput.vue"),
    StatementsAcceptance: () => import("@/components/StatementsAcceptance.vue"),
    PhoneInput: () => import("@/components/PhoneInput.vue"),
    BasketButton: () => import("@/components/basket/BasketButton.vue"),
  },
  data() {
    return {
      STEP,
      LANDING_MODE,
      TWO_FACTOR_MODE,
      currentStep: STEP.LANDING,
      landingMode: LANDING_MODE.REGISTRATION,
      twoFactorMode: TWO_FACTOR_MODE.REGISTRATION,
      newPassword: "",
      newPasswordValid: false,
      shrinkTransitionCompleted: false,
      moveTransitionCompleted: false,
      secondFactorAnswer: {
        code: "",
      },
      linkingError: false,
      redirectOnFinish: null,
      loaded: false,
      finishLoading: false,
      purchase: {},
      forgottenPasswordUser: "",
      chId: null,
    };
  },
  async created() {
    if (this.loggedIn) {
      await this.$store.dispatch("session/logout");
    }
  },
  async mounted() {
    try {
      await this.$store.dispatch("purchase/fetchPublic", this.purchaseId);
    } catch (ex) {
      console.warn(ex);
      await this.$router.push("/login");
      return;
    }
    this.purchase = this.$store.getters["purchase/getPublic"](this.purchaseId);

    if (this.purchase.customerPhone) {
      this.phone = this.purchase.customerPhone;
    }

    if (this.nameOnReg && this.purchase.customerName) {
      let split = this.purchase.customerName.split(" ");
      this.lastName = split[0];
      this.firstName = split[1];
    }

    this.loaded = true;
  },
  computed: {
    looksLikeAnEmail() {
      return this.forgottenPasswordUser.includes("@");
    },
  },
  methods: {
    async submitLanding() {
      if (this.landingMode === LANDING_MODE.REGISTRATION) {
        await this.submitRegister();
      } else if (this.landingMode === LANDING_MODE.LOGIN) {
        await this.submitLogin();
      } else if (this.landingMode === LANDING_MODE.FORGOT_PASSWORD) {
        await this.submitPasswordForgotten();
      }
    },
    async submitRegister() {
      let valid = await this.validateRegistration();
      if (!valid) {
        return;
      }

      try {
        this.loading = true;

        let request = this.createRequest();
        let secondFactorQuestion = await this.$store.dispatch(
          "session/register",
          request
        );
        this.secondFactorAnswer.id = secondFactorQuestion.id;
        this.currentStep = STEP.SECOND_FACTOR;
        this.loading = false;

        this.$nextTick(async () => {
          await resetValidator(this.$refs.codeObserver);
        });
      } catch (ex) {
        this.handleRegistrationException(
          ex,
          "af:merchandise.landing.error.already_exists"
        );
      }
    },
    async submitPasswordForgottenFinish() {
      let valid = await this.$refs.forgottenPasswordFinishObserver.validate();
      if (!valid) {
        return;
      }

      if (!this.passwordValid) return;

      try {
        this.finishLoading = true;

        this.redirectOnFinish = await this.finishPasswordChange({
          id: this.chId,
          nonce: this.nonce,
          code: this.secondFactorAnswer.code,
          newPassword: this.newPassword,
          landingParams: getActiveLandingParams(),
        });

        let linkingSuccess = await this.linkPurchase();

        this.linkingError = !linkingSuccess;
        this.loading = false;
        this.currentStep = STEP.SUMMARY;

        if (linkingSuccess) {
          window.setTimeout(() => {
            this.shrinkTransitionCompleted = true;
          }, 2500);

          window.setTimeout(() => {
            this.moveTransitionCompleted = true;
          }, 4000);
        }

        this.finishLoading = false;
      } catch (ex) {
        console.error(ex);
        this.finishLoading = false;
        if (ex.response.status === 400) {
          this.$buefy.toast.open({
            message: i18n.t("af:forget.new_password_cannot_be_old_password"),
            type: "is-danger",
          });
        } else {
          this.$buefy.toast.open({
            message: i18n.t("af:forget.error.general"),
            type: "is-danger",
          });
        }
      }
    },
    async submitPasswordForgotten() {
      let valid = await this.$refs.regObserver.validate();
      if (!valid) {
        return;
      }

      Vue.set(
        this,
        "forgottenPasswordUser",
        this.addPhonePadding(this.forgottenPasswordUser)
      );

      try {
        let reqResponse = await this.$store.dispatch(
          "session/requestPwdChange",
          {
            user: this.forgottenPasswordUser,
            nonce: this.nonce,
          }
        );
        this.chId = reqResponse.id;
        this.currentStep = STEP.SECOND_FACTOR;
        this.twoFactorMode = TWO_FACTOR_MODE.FORGOT_PASSWORD;
      } catch (ex) {
        console.error(ex);
        this.$buefy.toast.open({
          message: i18n.t("af:forget.error.general"),
          type: "is-danger",
        });
      }
    },
    async submitLogin() {
      let valid = await this.$refs.regObserver.validate();
      if (!valid) {
        return;
      }

      try {
        this.loading = true;
        let loginResponse = await this.$store.dispatch("session/login", {
          user: this.phone,
          password: this.password,
          nonce: this.nonce,
        });

        this.secondFactorAnswer.id =
          loginResponse.secondFactorResponse.secondFactorId;
        this.currentStep = STEP.SECOND_FACTOR;
        this.loading = false;

        if (
          loginResponse.status === LOGIN_STATUS.ENUM.REGISTRATION_INCOMPLETE
        ) {
          this.twoFactorMode = TWO_FACTOR_MODE.REGISTRATION;
        } else if (loginResponse.status === LOGIN_STATUS.ENUM.LOGIN_OK) {
          this.twoFactorMode = TWO_FACTOR_MODE.LOGIN;
        } else if (
          loginResponse.status === LOGIN_STATUS.ENUM.PASSWORD_CHANGE_REQUIRED
        ) {
          this.twoFactorMode = TWO_FACTOR_MODE.PASSWORD_CHANGE_REQUIRED;
        }

        this.$nextTick(async () => {
          await resetValidator(this.$refs.codeObserver);
        });
      } catch (ex) {
        console.warn(ex);
        this.loading = false;
        this.$buefy.toast.open({
          message: i18n.t("af:login.error.general"),
          type: "is-danger",
        });
      }
    },
    async submitSecondFactor() {
      let valid = await this.$refs.codeObserver.validate();

      if (this.twoFactorMode === TWO_FACTOR_MODE.PASSWORD_CHANGE_REQUIRED) {
        valid = valid && this.newPasswordValid;
      }

      if (!valid) return;

      try {
        this.loading = true;

        this.secondFactorAnswer.nonce = this.nonce;

        if (this.twoFactorMode === TWO_FACTOR_MODE.REGISTRATION) {
          await this.finishRegistration(this.secondFactorAnswer);
          this.redirectOnFinish = {
            name: "PreliminaryAssessment",
          };
        } else if (this.twoFactorMode === TWO_FACTOR_MODE.LOGIN) {
          this.redirectOnFinish = await this.finishLogin(
            this.secondFactorAnswer
          );
        } else if (
          this.twoFactorMode === TWO_FACTOR_MODE.PASSWORD_CHANGE_REQUIRED
        ) {
          (this.secondFactorAnswer.newPassword = this.newPassword),
            (this.redirectOnFinish = await this.finishPasswordChange(
              this.secondFactorAnswer
            ));
        }

        let linkingSuccess = await this.linkPurchase();

        this.linkingError = !linkingSuccess;
        this.loading = false;
        this.currentStep = STEP.SUMMARY;

        if (linkingSuccess) {
          window.setTimeout(() => {
            this.shrinkTransitionCompleted = true;
          }, 2500);

          window.setTimeout(() => {
            this.moveTransitionCompleted = true;
          }, 4000);
        }
      } catch (ex) {
        this.loading = false;
        if (ex.response.status === 400) {
          this.$buefy.toast.open({
            message: i18n.t("af:forget.new_password_cannot_be_old_password"),
            type: "is-danger",
          });
        } else {
          this.$buefy.toast.open({
            message: i18n.t("af:login.error.general"),
            type: "is-danger",
          });
        }
        console.error(ex);
      }
    },
    async linkPurchase() {
      try {
        await this.$store.dispatch("purchase/link", this.purchaseId);
        await this.$store.dispatch("profile/updateStatus", {
          status: "ONBOARDED",
          result: "SUCCESS",
        });
      } catch (ex) {
        console.error(ex);
        return false;
      }
      return true;
    },
    async submitNext() {
      await this.fetchOnLogin();
      await this.$router.push(this.redirectOnFinish);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/colors.scss";

.basket-button {
  right: -500px !important;
  transition: right 2s;

  &.basket-opened {
    display: flex;
    right: 0 !important;
  }
}

.merchandise-landing-container {
  display: flex;
  justify-content: center;
}

.merchandise-registration-container {
  max-width: 560px;
  min-width: 560px;
  padding: 32px 36px;
  margin-right: 2rem;
}

.merchandise-basket-container {
  .info-box {
    background: $grey-2;
    border-radius: 8px;
    padding: 1rem;
    max-width: 500px;

    .icon {
      color: $blue;
    }

    &.has-error {
      margin-top: 5rem;

      .icon {
        color: $red;
      }
    }
  }

  .af-modal-card {
    max-width: 500px;
    top: -4rem;
    right: 0;
    max-height: 100%;
    height: auto;
    box-shadow: rgba(100, 100, 111, 0.2) 0 7px 29px 0;
    margin: 0 0 0 2rem !important;
    transition: max-height 2s, right 2s, top 1s;
  }

  &-shrink {
    height: 400px;

    .af-modal-card {
      max-height: 0;
      overflow: hidden;
      padding-top: 0.5rem !important;
      padding-bottom: 2.25rem !important;
      top: 6rem;
    }
  }

  &-pushed-right {
    .af-modal-card {
      right: -60rem;
    }
  }
}

.nonce-info {
  margin-top: -1rem;
  margin-bottom: 2rem !important;
}

.forgot-password {
  margin-top: -1rem;
  font-size: 14px;
  margin-bottom: 1rem;
}

@media only screen and (max-width: 769px) {
  .basket-button {
    display: none !important;
  }

  .merchandise-landing-container {
    flex-direction: column;
    justify-content: left;
    overflow: hidden;
  }

  .merchandise-registration-container {
    min-width: 0;
    padding: 20px;
    margin-left: 0;
    margin-right: 0;
  }

  .merchandise-basket-container {
    .info-box {
      &.has-error {
        margin-top: 2rem;
      }
    }

    .af-modal-card {
      top: 0;
      margin-left: 0 !important;
      margin-right: 0 !important;
      max-width: 100%;
      box-shadow: none;
    }

    &-shrink {
      height: 200px;
    }

    &-pushed-right {
      .af-modal-card {
        right: -60rem;
      }
    }
  }
}
</style>
